import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { flattenToAppURL } from '@plone/volto/helpers';
import AppNav from '../AppNav/AppNav';
import MatterTitleField from '../MatterTitleField/MatterTitleField';
import { formatTokenMessage, sharedMessages } from '../../utils/uiMessages';

const CLOSED_STATUSES = new Set(['done', 'cancelled']);

const initialForm = {
  title: '',
  client_name: '',
  application_number: '',
  matter_reference_code: '',
  jurisdiction: '',
  matter_status: 'open',
  responsible_attorney: '',
  notes: '',
};

const messages = defineMessages({
  addNewJob: {
    id: 'Add new job',
    defaultMessage: 'Add new job',
  },
  clientName: {
    id: 'Client name',
    defaultMessage: 'Client name',
  },
  dueOn: {
    id: 'due {date}',
    defaultMessage: 'due {date}',
  },
  finishedJobs: {
    id: 'Finished jobs',
    defaultMessage: 'Finished jobs',
  },
  jobLookupFailed: {
    id: 'Job lookup failed with status {status}',
    defaultMessage: 'Job lookup failed with status {status}',
  },
  jobsForThisMatter: {
    id: 'Jobs for this matter',
    defaultMessage: 'Jobs for this matter',
  },
  loadingMatter: {
    id: 'Loading matter…',
    defaultMessage: 'Loading matter…',
  },
  matterDetail: {
    id: 'Matter detail',
    defaultMessage: 'Matter detail',
  },
  matterLookupFailed: {
    id: 'Matter lookup failed with status {status}',
    defaultMessage: 'Matter lookup failed with status {status}',
  },
  matterUpdated: {
    id: 'Matter updated.',
    defaultMessage: 'Matter updated.',
  },
  noFinishedJobs: {
    id: 'No finished jobs.',
    defaultMessage: 'No finished jobs.',
  },
  noMatterPathProvided: {
    id: 'No matter path was provided.',
    defaultMessage: 'No matter path was provided.',
  },
  noPendingJobs: {
    id: 'No pending jobs.',
    defaultMessage: 'No pending jobs.',
  },
  pendingFinishedSummary: {
    id: '{pending} pending · {finished} finished',
    defaultMessage: '{pending} pending · {finished} finished',
  },
  pendingJobs: {
    id: 'Pending jobs',
    defaultMessage: 'Pending jobs',
  },
  requestFailedWithStatus: {
    id: 'Request failed with status {status}',
    defaultMessage: 'Request failed with status {status}',
  },
  reviewAndEditMatter: {
    id: 'Review and edit matter information directly.',
    defaultMessage: 'Review and edit matter information directly.',
  },
  saving: {
    id: 'Saving…',
    defaultMessage: 'Saving…',
  },
  shortHours: {
    id: '{hours}h',
    defaultMessage: '{hours}h',
  },
});

export default function MatterEditorPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const matterPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const params = new URLSearchParams(window.location.search);
    return normalizeContentPath(params.get('path') || '');
  }, []);

  const [form, setForm] = useState(initialForm);
  const [original, setOriginal] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [finishedJobs, setFinishedJobs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function load() {
      if (!matterPath) {
        setError(intl.formatMessage(messages.noMatterPathProvided));
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/++api++${matterPath}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.matterLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        const normalized = normalizeMatter(payload);
        if (active) {
          setForm(normalized);
          setOriginal(normalized);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [intl, matterPath, token]);

  useEffect(() => {
    let active = true;

    async function loadJobs() {
      if (!matterPath) {
        if (active) {
          setPendingJobs([]);
          setFinishedJobs([]);
          setJobsLoading(false);
        }
        return;
      }

      setJobsLoading(true);
      try {
        const query = new URLSearchParams({
          portal_type: 'Patent Job',
          b_size: '200',
          'path.query': matterPath,
          'path.depth': '1',
          metadata_fields: 'internal_due_date',
        });
        query.append('metadata_fields', 'status');
        query.append('metadata_fields', 'estimated_hours');
        query.append('metadata_fields', 'title');
        query.append('metadata_fields', 'getURL');
        const response = await fetch(`/++api++/@search?${query.toString()}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.jobLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        const items = (payload.items || []).map(normalizeJobItem);
        const sorted = items.sort((left, right) => {
          const leftDue = left.internal_due_date || '9999-12-31';
          const rightDue = right.internal_due_date || '9999-12-31';
          return (
            leftDue.localeCompare(rightDue) || left.title.localeCompare(right.title)
          );
        });
        if (active) {
          setPendingJobs(sorted.filter((item) => !CLOSED_STATUSES.has(item.status)));
          setFinishedJobs(
            sorted
              .filter((item) => CLOSED_STATUSES.has(item.status))
              .sort((left, right) =>
                right.internal_due_date.localeCompare(left.internal_due_date),
              ),
          );
        }
      } catch (loadError) {
        if (active) {
          setPendingJobs([]);
          setFinishedJobs([]);
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setJobsLoading(false);
        }
      }
    }

    loadJobs();
    return () => {
      active = false;
    };
  }, [intl, matterPath, token]);

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const normalizedMatterReferenceCode = normalizeMatterReferenceCode(
        form.matter_reference_code,
      );
      const submitted = {
        ...form,
        matter_reference_code: normalizedMatterReferenceCode,
      };
      const response = await fetch(`/++api++${matterPath}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
        },
        body: JSON.stringify({
          title: submitted.title,
          client_name: submitted.client_name || '',
          application_number: submitted.application_number || '',
          matter_reference_code: normalizedMatterReferenceCode,
          matter_reference: normalizedMatterReferenceCode,
          jurisdiction: submitted.jurisdiction || '',
          matter_status: submitted.matter_status || 'open',
          responsible_attorney: submitted.responsible_attorney || '',
          notes: submitted.notes || '',
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }

      let normalized = submitted;
      if (response.status !== 204) {
        const payload = await response.json();
        normalized = normalizeMatter(payload);
      }
      setForm(normalized);
      setOriginal(normalized);
      setSuccess(intl.formatMessage(messages.matterUpdated));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  function onCancel() {
    setForm(original);
    setError('');
    setSuccess('');
  }

  return (
    <main className="patent-entry">
      <section className="patent-entry__shell">
        <AppNav currentPath="/matters" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">
            {intl.formatMessage(sharedMessages.matters)}
          </p>
          <h1>{form.title || intl.formatMessage(messages.matterDetail)}</h1>
          <p className="patent-entry__note">
            {intl.formatMessage(messages.reviewAndEditMatter)}
          </p>
          <p className="patent-entry__note">
            <Link className="patent-tool__backlink" to="/matters">
              {intl.formatMessage(sharedMessages.backToMatters)}
            </Link>
            {' · '}
            <Link className="patent-tool__backlink" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
          </p>
        </header>

        {!loading && !error ? (
          <section className="patent-entry__pending-jobs">
            <div className="patent-entry__pending-jobs-head">
              <strong>{intl.formatMessage(messages.jobsForThisMatter)}</strong>
              {jobsLoading ? (
                <span className="patent-entry__note">
                  {intl.formatMessage(sharedMessages.loading)}
                </span>
              ) : (
                <span className="patent-entry__note">
                  {intl.formatMessage(messages.pendingFinishedSummary, {
                    pending: pendingJobs.length,
                    finished: finishedJobs.length,
                  })}
                </span>
              )}
            </div>

            <div className="patent-section__group">
              <div className="patent-dashboard__section-header">
                <div>
                  <h2>{intl.formatMessage(messages.pendingJobs)}</h2>
                </div>
              </div>
              {pendingJobs.length ? (
                <ul className="patent-entry__pending-jobs-list">
                  {pendingJobs.map((item) => (
                    <li key={`${item.url}-${item.title}`} className="patent-entry__pending-job">
                      <Link
                        className="patent-entry__pending-job-title"
                        to={`/job-editor?path=${encodeURIComponent(
                          normalizeContentPath(item.url),
                        )}`}
                      >
                        {item.title}
                      </Link>
                      <span className="patent-entry__pending-job-meta">
                        {formatJobMeta(intl, item)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="patent-entry__note">
                  {intl.formatMessage(messages.noPendingJobs)}
                </p>
              )}
            </div>

            <div className="patent-section__group">
              <div className="patent-dashboard__section-header">
                <div>
                  <h2>{intl.formatMessage(messages.finishedJobs)}</h2>
                </div>
              </div>
              {finishedJobs.length ? (
                <ul className="patent-entry__pending-jobs-list">
                  {finishedJobs.map((item) => (
                    <li key={`${item.url}-${item.title}`} className="patent-entry__pending-job">
                      <Link
                        className="patent-entry__pending-job-title"
                        to={`/job-editor?path=${encodeURIComponent(
                          normalizeContentPath(item.url),
                        )}`}
                      >
                        {item.title}
                      </Link>
                      <span className="patent-entry__pending-job-meta">
                        {formatJobMeta(intl, item)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="patent-entry__note">
                  {intl.formatMessage(messages.noFinishedJobs)}
                </p>
              )}
            </div>
          </section>
        ) : null}

        {loading ? (
          <p className="patent-entry__note">
            {intl.formatMessage(messages.loadingMatter)}
          </p>
        ) : null}
        {error && !loading ? <p className="patent-entry__error">{error}</p> : null}

        {!loading && !error ? (
          <form className="patent-entry__form" onSubmit={onSave}>
            <TextField
              label={intl.formatMessage(sharedMessages.matterReferenceCode)}
              value={form.matter_reference_code}
              onChange={(value) =>
                setForm((current) => ({ ...current, matter_reference_code: value }))
              }
            />
            <MatterTitleField
              value={form.title}
              onChange={(value) => setForm((current) => ({ ...current, title: value }))}
            />
            <TextField
              label={intl.formatMessage(messages.clientName)}
              value={form.client_name}
              onChange={(value) =>
                setForm((current) => ({ ...current, client_name: value }))
              }
            />
            <TextField
              label={intl.formatMessage(sharedMessages.applicationNumber)}
              value={form.application_number}
              onChange={(value) =>
                setForm((current) => ({ ...current, application_number: value }))
              }
            />
            <TextField
              label={intl.formatMessage(sharedMessages.jurisdiction)}
              value={form.jurisdiction}
              onChange={(value) =>
                setForm((current) => ({ ...current, jurisdiction: value }))
              }
            />
            <SelectField
              label={intl.formatMessage(sharedMessages.matterStatus)}
              value={form.matter_status}
              onChange={(value) =>
                setForm((current) => ({ ...current, matter_status: value }))
              }
              options={[
                'open',
                'awaiting_instruction',
                'active_prosecution',
                'allowed',
                'abandoned',
                'granted',
                'closed',
              ]}
            />
            <TextField
              label={intl.formatMessage(sharedMessages.responsibleAttorney)}
              value={form.responsible_attorney}
              onChange={(value) =>
                setForm((current) => ({ ...current, responsible_attorney: value }))
              }
            />
            <TextAreaField
              label={intl.formatMessage(sharedMessages.notes)}
              value={form.notes}
              onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
            />

            {success ? <p className="patent-entry__success">{success}</p> : null}

            <div className="patent-entry__actions patent-entry__actions--row">
              <button className="patent-entry__submit" type="submit" disabled={saving}>
                {saving
                  ? intl.formatMessage(messages.saving)
                  : intl.formatMessage(sharedMessages.save)}
              </button>
              <Link
                className="patent-entry__ghost"
                to={`/register-job?matter_path=${encodeURIComponent(matterPath)}`}
              >
                {intl.formatMessage(messages.addNewJob)}
              </Link>
              <button
                className="patent-entry__ghost"
                type="button"
                onClick={onCancel}
                disabled={saving}
              >
                {intl.formatMessage(sharedMessages.cancelChanges)}
              </button>
            </div>
            <div className="patent-entry__actions patent-entry__actions--row">
              <Link className="patent-tool__backlink" to="/matters">
                {intl.formatMessage(sharedMessages.backToMatters)}
              </Link>
              <Link className="patent-tool__backlink" to="/overview">
                {intl.formatMessage(sharedMessages.backToOverview)}
              </Link>
            </div>
          </form>
        ) : null}
      </section>
    </main>
  );
}

function normalizeJobItem(item) {
  return {
    title: item.title || '',
    url: item.getURL || item['@id'] || item.url || '',
    internal_due_date: item.internal_due_date || '',
    status: item.status?.token || item.status || 'not_started',
    estimated_hours: item.estimated_hours ?? 0,
  };
}

function normalizeContentPath(path) {
  if (!path) {
    return '';
  }
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(flattenToAppURL(path), origin).pathname;
  } catch {
    return flattenToAppURL(path);
  }
}

function normalizeMatterReferenceCode(value) {
  const trimmed = String(value || '').trim();
  return trimmed || 'not assigned';
}

function normalizeMatter(payload) {
  return {
    title: payload.title || '',
    client_name: payload.client_name || '',
    application_number: payload.application_number || '',
    matter_reference_code:
      payload.matter_reference_code || payload.matter_reference || 'not assigned',
    jurisdiction: payload.jurisdiction || '',
    matter_status: Array.isArray(payload.matter_status)
      ? payload.matter_status[0] || 'open'
      : payload.matter_status || 'open',
    responsible_attorney: payload.responsible_attorney || '',
    notes: payload.notes || '',
  };
}

function TextField({ label, value, onChange, disabled = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, disabled = false }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className="patent-entry__textarea"
        rows="6"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, disabled = false }) {
  const intl = useIntl();

  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <select
        className="patent-entry__input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatTokenMessage(intl, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatJobMeta(intl, item) {
  const parts = [formatTokenMessage(intl, item.status)];
  if (item.internal_due_date) {
    parts.push(
      intl.formatMessage(messages.dueOn, {
        date: item.internal_due_date,
      }),
    );
  }
  if (item.estimated_hours) {
    parts.push(
      intl.formatMessage(messages.shortHours, {
        hours: item.estimated_hours,
      }),
    );
  }
  return parts.join(' · ');
}

async function extractError(response, intl) {
  try {
    const payload = await response.json();
    return (
      payload.message ||
      payload.error?.message ||
      intl.formatMessage(messages.requestFailedWithStatus, {
        status: response.status,
      })
    );
  } catch {
    return intl.formatMessage(messages.requestFailedWithStatus, {
      status: response.status,
    });
  }
}
